import { DatePicker, Radio } from 'antd';
import type { RadioChangeEvent, TimeRangePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import IsoWeek from 'dayjs/plugin/isoWeek';
import QuarterOfYear from 'dayjs/plugin/quarterOfYear';
import updateLocale from 'dayjs/plugin/updateLocale';
import UTC from 'dayjs/plugin/utc';
import WeekOfYear from 'dayjs/plugin/weekOfYear';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@material-ui/core';

import { AggregateBy } from '../../types';

export interface DateRangeSelectProps {
  defaultTimePeriod: AggregateBy;
  showDailyAggregation?: boolean;
  initialRange?: {
    start: string;
    end: string;
    aggregateBy: AggregateBy;
  };
  onDateRangeChange: ({
    start,
    end,
    aggregateBy,
  }: {
    start: string;
    end: string;
    aggregateBy: 'day' | 'week' | 'month' | 'quarter';
  }) => void;
}

dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekStart: 1,
});
dayjs.extend(WeekOfYear);
dayjs.extend(QuarterOfYear);
dayjs.extend(IsoWeek);
dayjs.extend(UTC);

const getDefaultValue = (period: AggregateBy): [Dayjs, Dayjs] => {
  if (period === 'day') {
    return [
      dayjs.utc().subtract(6, 'day').startOf('day'),
      dayjs.utc().endOf('day'),
    ];
  } else if (period === 'week') {
    return [
      dayjs.utc().subtract(2, 'week').startOf('isoWeek'),
      dayjs.utc().endOf('isoWeek'),
    ];
  } else if (period === 'month') {
    return [
      dayjs.utc().subtract(2, 'month').startOf('month'),
      dayjs.utc().endOf('month'),
    ];
  }
  return [
    dayjs.utc().subtract(2, 'quarter').startOf('quarter'),
    dayjs.utc().endOf('quarter'),
  ];
};

const toIsoString = (date: Dayjs) => {
  return `${date.format('YYYY-MM-DDTHH:mm:ss')}Z`;
};

const endOfToday = () => dayjs.utc().endOf('day');

const dayRangePresets: TimeRangePickerProps['presets'] = [
  {
    label: 'Today',
    value: [dayjs.utc().startOf('day'), endOfToday()],
  },
  {
    label: 'Yesterday',
    value: [
      dayjs.utc().subtract(1, 'day').startOf('day'),
      dayjs.utc().subtract(1, 'day').endOf('day'),
    ],
  },
  {
    label: 'Last 7 days',
    value: [dayjs.utc().subtract(6, 'day').startOf('day'), endOfToday()],
  },
  {
    label: 'Last 14 days',
    value: [dayjs.utc().subtract(13, 'day').startOf('day'), endOfToday()],
  },
];

const weekRangePresets: TimeRangePickerProps['presets'] = [
  {
    label: 'This week',
    value: [dayjs.utc().startOf('isoWeek'), endOfToday()],
  },
  {
    label: 'Last week',
    value: [
      dayjs.utc().subtract(1, 'week').startOf('isoWeek'),
      dayjs.utc().subtract(1, 'week').endOf('isoWeek'),
    ],
  },
  {
    label: 'Last 2 weeks',
    value: [dayjs.utc().subtract(1, 'week').startOf('isoWeek'), endOfToday()],
  },
  {
    label: 'Last 4 weeks',
    value: [dayjs.utc().subtract(3, 'week').startOf('isoWeek'), endOfToday()],
  },
  {
    label: 'Last 8 weeks',
    value: [dayjs.utc().subtract(7, 'week').startOf('isoWeek'), endOfToday()],
  },
];

const monthRangePresets: TimeRangePickerProps['presets'] = [
  {
    label: 'This month',
    value: [dayjs.utc().startOf('month'), endOfToday()],
  },
  {
    label: 'Last month',
    value: [
      dayjs.utc().subtract(1, 'month').startOf('month'),
      dayjs.utc().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    label: 'Last 3 months',
    value: [dayjs.utc().subtract(2, 'month').startOf('month'), endOfToday()],
  },
  {
    label: 'Last 6 months',
    value: [dayjs.utc().subtract(5, 'month').startOf('month'), endOfToday()],
  },
  {
    label: 'Last 12 months',
    value: [dayjs.utc().subtract(11, 'month').startOf('month'), endOfToday()],
  },
];

const quarterRangePresets: TimeRangePickerProps['presets'] = [
  {
    label: 'This quarter',
    value: [dayjs.utc().startOf('quarter'), endOfToday()],
  },
  {
    label: 'Last quarter',
    value: [
      dayjs.utc().subtract(1, 'quarter').startOf('quarter'),
      dayjs.utc().subtract(1, 'quarter').endOf('quarter'),
    ],
  },
  {
    label: 'Last 2 quarters',
    value: [
      dayjs.utc().subtract(1, 'quarter').startOf('quarter'),
      endOfToday(),
    ],
  },
  {
    label: 'Last 4 quarters',
    value: [
      dayjs.utc().subtract(3, 'quarter').startOf('quarter'),
      endOfToday(),
    ],
  },
];

const PickerForPeriod = ({
  period,
  defaultValue,
  onChange,
}: {
  period: AggregateBy;
  defaultValue: [Dayjs, Dayjs];
  onChange: (date: any) => void;
}) => {
  const disabledDate = (current: Dayjs) => {
    return current && current > dayjs.utc().endOf('day');
  };

  const preset = useMemo<TimeRangePickerProps['presets']>(() => {
    if (period === 'day') {
      return dayRangePresets;
    } else if (period === 'week') {
      return weekRangePresets;
    } else if (period === 'month') {
      return monthRangePresets;
    }
    return quarterRangePresets;
  }, [period]);

  return (
    <DatePicker.RangePicker
      presets={preset}
      key={period}
      defaultValue={defaultValue}
      onChange={onChange}
      picker={period === 'day' ? undefined : period}
      style={{ width: 270, minWidth: 270 }}
      disabledDate={disabledDate}
      allowClear={false}
    />
  );
};

export const DateRangeSelect: React.FC<DateRangeSelectProps> = ({
  onDateRangeChange,
  defaultTimePeriod,
  showDailyAggregation,
  initialRange,
}) => {
  const initialPeriod = initialRange?.aggregateBy ?? defaultTimePeriod;

  const initialValue = useMemo<[Dayjs, Dayjs]>(
    () =>
      initialRange
        ? [dayjs.utc(initialRange.start), dayjs.utc(initialRange.end)]
        : getDefaultValue(defaultTimePeriod),
    [initialRange, defaultTimePeriod],
  );

  const [period, setPeriod] = useState<AggregateBy>(initialPeriod);

  // While the period still matches the incoming range, show its dates;
  // otherwise fall back to the selected period's default window.
  const currentDefaultValue =
    initialRange && period === initialPeriod
      ? initialValue
      : getDefaultValue(period);

  const onChange = ([startDate, endDate]: [Dayjs, Dayjs]) => {
    onDateRangeChange({
      start: toIsoString(startDate.startOf(period)),
      end: toIsoString(endDate.endOf(period)),
      aggregateBy: period,
    });
  };

  useEffect(() => {
    // Seed the consumer. Prefer the exact incoming range (e.g. from the URL)
    // so shared links reproduce precisely; otherwise emit the default window.
    if (initialRange) {
      onDateRangeChange({
        start: initialRange.start,
        end: initialRange.end,
        aggregateBy: initialRange.aggregateBy,
      });
      return;
    }
    onDateRangeChange({
      start: toIsoString(initialValue[0]),
      end: toIsoString(initialValue[1]),
      aggregateBy: defaultTimePeriod,
    });
  }, [onDateRangeChange, initialValue, defaultTimePeriod, initialRange]);

  useEffect(() => {
    // Day aggregation is only valid while showDailyAggregation is on (e.g. the
    // Cost-KaaP tab). If it's selected and that tab loses focus, fall back to
    // the default period rather than leaving other tabs on daily data.
    if (!showDailyAggregation && period === 'day') {
      setPeriod(defaultTimePeriod);
      const newDefaultValue = getDefaultValue(defaultTimePeriod);
      onDateRangeChange({
        start: toIsoString(newDefaultValue[0].startOf(defaultTimePeriod)),
        end: toIsoString(newDefaultValue[1].endOf(defaultTimePeriod)),
        aggregateBy: defaultTimePeriod,
      });
    }
  }, [showDailyAggregation, period, defaultTimePeriod, onDateRangeChange]);

  const handlePeriodChange = useCallback(
    (e: RadioChangeEvent) => {
      const newPeriod = e.target.value;
      setPeriod(newPeriod);

      const newDefaultValue = getDefaultValue(newPeriod);
      onDateRangeChange({
        start: toIsoString(newDefaultValue[0].startOf(newPeriod)),
        end: toIsoString(newDefaultValue[1].endOf(newPeriod)),
        aggregateBy: newPeriod,
      });
    },
    [onDateRangeChange],
  );

  return (
    <>
      <style>
        {`
          .ant-picker-dropdown .ant-picker-presets {
            min-height: 200px !important;
            overflow: visible !important;
          }
        `}
      </style>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-end',
          maxWidth: '600px',
        }}
      >
        <Radio.Group
          value={period}
          onChange={handlePeriodChange}
          style={{
            minWidth: showDailyAggregation ? 280 : 220,
            marginRight: 15,
            flexWrap: 'nowrap',
            display: 'flex',
          }}
        >
          {showDailyAggregation && <Radio.Button value="day">Day</Radio.Button>}
          <Radio.Button value="week">Week</Radio.Button>
          <Radio.Button value="month">Month</Radio.Button>
          <Radio.Button value="quarter">Quarter</Radio.Button>
        </Radio.Group>
        <PickerForPeriod
          period={period}
          defaultValue={currentDefaultValue}
          onChange={onChange}
        />
      </Box>
    </>
  );
};
